import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@components/Modal/Modal';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { AddPresetLayerIcon } from '@components/icons/icons/AddPresetLayerIcon';
import * as SelectBox from '@components/form/SelectBox/SelectBox';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { presetsApi } from '@api/presetsApi/presetsApi';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectCurrentProjectId } from '@redux/selectors/selectors';
import { Form, Row } from '@components/form/Form/Form';
import Tooltip from '@mui/material/Tooltip';
import { entityMapLayersToSourcesAndLayers } from '@utils/mapUtils';
import { addedLayers, addedSources } from '@redux/map/map-reducer';
import { useToastContext } from '@context/toastContext';
import { useDispatch } from 'react-redux';
import {
    PRESET_BY_ENTITY_NAME_KEY,
    PRESET_ENTITY_NAME_BY_PRESET_TYPE_KEY,
    PresetEntityName,
    PresetTypeKey,
} from '@api/presetsApi/types';
import { Option } from '@components/form/SelectBox/SelectBox';
import { themeColors } from '@styles/theme';
import { useTheme } from '@context/themeContext';
import mapApi from '@api/mapApi';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { usePresetsContext } from '@context/presetsContext';

const presetTypes: PresetTypeKey[] = [
    'publicTransit',
    'evPreset',
    'maasPreset',
    'scenarioCasePreset',
    'roadPreset',
];
const presetNamesByTypes: Record<PresetTypeKey, string> = {
    publicTransit: 'Public transit preset',
    evPreset: 'EV preset',
    maasPreset: 'MaaS preset',
    scenarioCasePreset: 'Scenario case preset',
    roadPreset: 'Road preset',
};

const filterCurPresetFromPresets = (curPresetId: string, presetsOptions: Option[] = []) =>
    presetsOptions.filter(({ value }) => value !== curPresetId);

export const NewLayerFromPreset = React.memo(() => {
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const projectId = useTypedSelector(selectCurrentProjectId);
    const { selectedPresetEntityName, selectedPresetId } = usePresetsContext();
    const { t } = useTranslation();

    const curPresetKey = useMemo(
        () => PRESET_BY_ENTITY_NAME_KEY[selectedPresetEntityName],
        [selectedPresetEntityName]
    );

    const presetTypesOptions: Option[] = useMemo(
        () =>
            presetTypes.map(type => ({
                value: type,
                caption: presetNamesByTypes[type],
            })),
        [presetTypes]
    );

    const [presetsOptions, setPresetsOptions] = useState<{ [key in PresetTypeKey]: Option[] }>(
        {} as { [key in PresetTypeKey]: Option[] }
    );
    const [presetsForTypeOptions, setPresetsForTypeOptions] = useState<Option[]>([]);

    const dispatch = useDispatch();
    const { addToast } = useToastContext();
    const { theme } = useTheme();

    const handleClick = useCallback(async () => {
        const resp = await presetsApi.getProjectPresets({
            projectId,
            abortSignal: new AbortController().signal,
        });

        if (!isErrorResponse(resp)) {
            let defaultPresets: Option[] = [];
            setPresetsOptions(prev =>
                presetTypes.reduce((acc, type) => {
                    if (resp[type]) {
                        acc[type] = resp[type].map(preset => ({
                            value: preset.id,
                            caption: preset.name,
                        }));
                        if (!defaultPresets.length) {
                            defaultPresets = acc[type];
                        }
                    }
                    return acc;
                }, prev)
            );
            setPresetsForTypeOptions(filterCurPresetFromPresets(selectedPresetId, defaultPresets));
        }

        setIsOpen(true);
    }, [selectedPresetEntityName, selectedPresetId, presetTypes, setIsOpen]);

    const handleTypeChange = useCallback(
        async event => {
            const { value } = event.target;
            setPresetsForTypeOptions(prev => {
                if (value === curPresetKey) {
                    return filterCurPresetFromPresets(selectedPresetId, presetsOptions[value]);
                }
                return presetsOptions[value];
            });
        },
        [curPresetKey, selectedPresetId, projectId, presetsOptions]
    );

    const onFormSubmit = useCallback(
        async event => {
            const formData = new FormData(event.target);

            const abortController = new AbortController();
            const presetEntity = formData.get('presetEntity') as PresetEntityName;
            const presetId = formData.get('presetId') as string;

            if (presetEntity && PRESET_ENTITY_NAME_BY_PRESET_TYPE_KEY[presetEntity] && presetId) {
                setLoading(true);

                const entityMapLayers = await mapApi.getLayersForPreset(
                    {
                        presetEntity: PRESET_ENTITY_NAME_BY_PRESET_TYPE_KEY[presetEntity],
                        presetId,
                    },
                    abortController.signal
                );

                if (isErrorResponse(entityMapLayers)) {
                    const parsedErr =
                        typeof entityMapLayers.error === 'string'
                            ? safelyParseJson(entityMapLayers.error)?.error
                            : entityMapLayers.error.error;
                    addToast(parsedErr, 'error');
                } else {
                    const { sources, layers } = entityMapLayersToSourcesAndLayers(
                        entityMapLayers,
                        true
                    );
                    dispatch(addedSources(sources));
                    dispatch(addedLayers(layers));

                    addToast(t('layers-list.add_preset_layer_success_msg'), 'success');
                }

                setLoading(false);
            } else {
                console.error(
                    'Replan. NewLayerFromPreset.onFormSubmit. Invalid presetEntity or presetId: ',
                    presetEntity,
                    presetId
                );
            }

            setIsOpen(false);
        },
        [projectId]
    );

    return (
        <>
            <Tooltip title={t('layers-list.add_preset_layer_btn')} placement="top">
                <IconButton
                    icon={AddPresetLayerIcon}
                    width={36}
                    height={36}
                    onClick={handleClick}
                    iconColor={theme === 'light' ? themeColors.navyBlue : themeColors.white}
                    backgroundColor={theme === 'light' ? themeColors.white : themeColors.grey5}
                />
            </Tooltip>
            {isOpen && (
                <Modal title={t('layers-list.add_preset_form_title')} setVisible={setIsOpen} narrow>
                    <LoadingBackdrop isVisible={loading}>
                        <Form
                            name="layerUploadForm"
                            onSubmit={onFormSubmit}
                            submitBtnTitle={t('layers-list.add_preset_form_submit_btn')}
                            disabled={!presetsForTypeOptions.length}
                        >
                            <Row>
                                <SelectBox.Labeled
                                    label={t('layers-list.add_preset_form_field_presets_type')}
                                    name="presetEntity"
                                    onChange={handleTypeChange}
                                    options={presetTypesOptions}
                                />
                            </Row>
                            <Row>
                                <SelectBox.Labeled
                                    label={t('layers-list.add_preset_form_field_presets-label')}
                                    name="presetId"
                                    options={presetsForTypeOptions}
                                />
                            </Row>
                        </Form>
                    </LoadingBackdrop>
                </Modal>
            )}
        </>
    );
});
